// Uniform-grid spatial index (spec §5.2): cell key "cx,cy" → ids. Used for
// viewport culling, hit testing, marquee selection and snap candidates.
import { intersects, type Rect } from '../util/geometry.ts';

interface Entry { rect: Rect; x0: number; y0: number; x1: number; y1: number; oversized: boolean }

/** Objects spanning more cells than this live in a side list checked on every query. */
const MAX_CELLS_PER_OBJECT = 1024;

export class SpatialIndex {
  private readonly cellSize: number;
  private readonly cells = new Map<string, Set<string>>();
  private readonly entries = new Map<string, Entry>();
  private readonly oversized = new Set<string>();

  constructor(cellSize = 512) {
    this.cellSize = cellSize;
  }

  get size(): number { return this.entries.size; }

  getBounds(id: string): Rect | undefined {
    return this.entries.get(id)?.rect;
  }

  update(id: string, rect: Rect): void {
    const cs = this.cellSize;
    const x0 = Math.floor(rect.x / cs), y0 = Math.floor(rect.y / cs);
    const x1 = Math.floor((rect.x + rect.w) / cs), y1 = Math.floor((rect.y + rect.h) / cs);
    const prev = this.entries.get(id);
    if (prev && prev.x0 === x0 && prev.y0 === y0 && prev.x1 === x1 && prev.y1 === y1) {
      prev.rect = rect;
      return;
    }
    if (prev) this.unlink(id, prev);
    const oversized = (x1 - x0 + 1) * (y1 - y0 + 1) > MAX_CELLS_PER_OBJECT;
    const entry: Entry = { rect, x0, y0, x1, y1, oversized };
    this.entries.set(id, entry);
    if (oversized) {
      this.oversized.add(id);
      return;
    }
    for (let cx = x0; cx <= x1; cx++) {
      for (let cy = y0; cy <= y1; cy++) {
        const key = `${cx},${cy}`;
        let set = this.cells.get(key);
        if (!set) this.cells.set(key, (set = new Set()));
        set.add(id);
      }
    }
  }

  remove(id: string): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    this.unlink(id, entry);
    this.entries.delete(id);
  }

  /** Ids whose bounds intersect `r` (unordered). */
  query(r: Rect): string[] {
    const cs = this.cellSize;
    const x0 = Math.floor(r.x / cs), y0 = Math.floor(r.y / cs);
    const x1 = Math.floor((r.x + r.w) / cs), y1 = Math.floor((r.y + r.h) / cs);
    const out: string[] = [];

    // A huge query rect (zoomed far out) visits more cells than exist; scan entries instead.
    if ((x1 - x0 + 1) * (y1 - y0 + 1) > this.cells.size) {
      for (const [id, e] of this.entries) if (intersects(e.rect, r)) out.push(id);
      return out;
    }

    const seen = new Set<string>();
    for (let cx = x0; cx <= x1; cx++) {
      for (let cy = y0; cy <= y1; cy++) {
        const set = this.cells.get(`${cx},${cy}`);
        if (!set) continue;
        for (const id of set) {
          if (seen.has(id)) continue;
          seen.add(id);
          if (intersects(this.entries.get(id)!.rect, r)) out.push(id);
        }
      }
    }
    for (const id of this.oversized) {
      if (intersects(this.entries.get(id)!.rect, r)) out.push(id);
    }
    return out;
  }

  private unlink(id: string, e: Entry): void {
    if (e.oversized) {
      this.oversized.delete(id);
      return;
    }
    for (let cx = e.x0; cx <= e.x1; cx++) {
      for (let cy = e.y0; cy <= e.y1; cy++) {
        const key = `${cx},${cy}`;
        const set = this.cells.get(key);
        if (!set) continue;
        set.delete(id);
        if (!set.size) this.cells.delete(key);
      }
    }
  }
}
